import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { Row } from 'react-table';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useGetAppointmentColorsEnabled } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import {
  Chip,
  currency,
  Table,
  TableBulkActions,
  TableColumnConfig,
  TableRowActions,
  Text,
  useTooltip,
} from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../constants';
import { useScheduleSettingsSmallMode } from '../../hooks/settings/use-schedule-settings-small-mode';
import { useDataSourceColumn } from '../../hooks/use-data-source-column';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { useAppointmentTypesHelperShallowStore } from '../../stores/use-appointment-types-helper-store';
import { SETTINGS_TRACKING_IDS } from '../../trackingIds/settings-tracking-ids';
import { ColorDot } from '../appointment-panel/color-dot';
import { VisibilityToggle } from '../ManagePractitionersV3/VisibilityToggle';
import { ProvidersChip } from './providers-chip';
import { AppointmentTypesTableProps } from './types';

type AppointmentTypesLargeTableProps = Omit<AppointmentTypesTableProps, 'bulkActions' | 'rowActions'> & {
  isPaymentsEnabled?: boolean;
  isMultiLocation: boolean;
  bulkActions?: TableBulkActions<AppointmentType>[];
  rowActions?: TableRowActions<AppointmentType>;
};

export const AppointmentTypesLargeTable = ({
  locationId,
  isLoading,
  mappingList,
  pageConfig,
  debouncedSearchValue,
  setPageConfig,
  isPaymentsEnabled = false,
  setSelectedAppointmentTypes,
  handleTableRefetch,
  bulkActions,
  rowActions,
  isMultiLocation,
}: AppointmentTypesLargeTableProps) => {
  const { t } = useTranslation('scheduleAppointmentTypes');
  const { isSmallMode } = useScheduleSettingsSmallMode();
  const { getOnlineSchedulingReadyLocationIds } = useSchedulingLocationInfo();
  const isOnlineSchedulingEnabled = getOnlineSchedulingReadyLocationIds().length > 0;
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();
  const { canUseAppointmentColors } = useGetAppointmentColorsEnabled();
  const listLocationSettingsQuery = SchedulerV3.Queries.useListLocationSettingsQuery({
    request: { locationIds: [locationId] },
    options: {
      enabled: !!locationId,
    },
  });
  const globalBookingDeposit = listLocationSettingsQuery.data?.locationsSettings?.[locationId]?.bookingDeposit;
  const { data, isLoading: isLoadingAppointmentTypesData } = SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
    debouncedSearchValue,
    pageConfig,
    selectedLocationId: locationId,
    selectedLocationIds: [locationId],
    childLocationId: isMultiLocation ? locationId : '',
  });
  isLoading = isLoading || isLoadingAppointmentTypesData;
  const totalCount = data?.totalCount;
  const handleSearch = useCallback(
    (searchValue: string) => {
      const trimmedSearchValue = searchValue.trim();
      if (totalCount) {
        setPageConfig((prev) => ({ ...prev, page: 1, searchValue: trimmedSearchValue }));
      } else if (searchValue.length === 0) {
        setPageConfig((prev) => ({ ...prev, searchValue: trimmedSearchValue }));
      }
    },
    [setPageConfig, totalCount]
  );

  const {
    shouldShowColumn: showSourceColumn,
    cellRenderer: sourceColumnCellRenderer,
    accessor: sourceColumnAccessor,
  } = useDataSourceColumn<AppointmentType>({
    sourceIdField: 'sourceId',
  });

  const tableColumns = useMemo(
    (): TableColumnConfig<AppointmentType>[] => [
      {
        Header: t('Color'),
        id: 'appointmentTypeColor',
        omit: !canUseAppointmentColors,
        accessor: (row: AppointmentType) => row,
        cellRenderer: (cellValue: AppointmentType) => (
          <ColorDot
            size='large'
            color={cellValue?.details?.clr || PRIMARY_EVENT_COLOR}
            {...(!isSmallMode ? { style: { margin: 'auto' } } : {})}
          />
        ),
        disableSortBy: true,
        maxWidth: 70,
      },
      {
        Header: <DisplayNameHeader />,
        id: 'displayName',
        accessor: (row) => row?.displayName || '-',
        width: 250,
        minWidth: 200,
      },
      {
        Header: t('Appointment Type Name'),
        id: 'appointmentTypeName',
        omit: !isIntegratedOffice,
        accessor: (row: AppointmentType) => row?.externalName || '-',
        disableSortBy: true,
        width: 250,
        minWidth: 200,
      },
      {
        Header: t('Duration'),
        id: 'durationMinutes',
        accessor: (row) => (row.details?.dur ? `${row.details.dur} ${t('Minutes')}` : '-'),
        width: 100,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        Header: t('Providers'),
        id: 'providers',
        accessor: (row: AppointmentType) => row.id,
        cellRenderer: (appointmentTypeId: string) => (
          <ProvidersChip appointmentTypeId={appointmentTypeId} mappingList={mappingList} />
        ),
        disableSortBy: true,
        minWidth: 300,
        maxWidth: 650,
      },
      {
        Header: t('Payment Amount'),
        id: 'paymentAmount',
        omit: !isPaymentsEnabled,
        disableSortBy: true,
        accessor: (row) => row.details?.bda,
        cellRenderer: (amount) =>
          globalBookingDeposit
            ? currency(globalBookingDeposit, { decimals: 2 })
            : amount
            ? currency(amount, { decimals: 2 })
            : '-',
        minWidth: 150,
        maxWidth: 150,
      },
      {
        Header: t('Status'),
        id: 'archiveStatus',
        omit: isIntegratedOffice,
        accessor: (row: AppointmentType) => row?.details?.act,
        minWidth: 100,
        maxWidth: 200,
        sortType: (a: Row<AppointmentType>, b: Row<AppointmentType>) =>
          Number(a?.original?.details?.act || 0) - Number(b?.original?.details?.act || 0),
        cellRenderer: (cellValue: AppointmentType) => (
          <Chip variant={!!cellValue ? 'primary' : 'disabled'}>{!!cellValue ? 'Active' : 'Archived'}</Chip>
        ),
      },
      {
        Header: t('Procedure Codes'),
        id: 'procedureCodes',
        omit: !isIntegratedOffice,
        accessor: (row: AppointmentType) => row?.details?.proc?.join(', ') || '-',
        disableSortBy: true,
        minWidth: 100,
        maxWidth: 200,
      },
      {
        Header: t('Source'),
        id: 'source',
        omit: !showSourceColumn,
        accessor: sourceColumnAccessor,
        cellRenderer: sourceColumnCellRenderer,
        minWidth: 120,
        maxWidth: 180,
      },
      {
        Header: t('Online Scheduling'),
        id: 'onlineScheduling',
        omit: !isOnlineSchedulingEnabled,
        accessor: (row: AppointmentType) => row,
        cellRenderer: (cellValue: AppointmentType) => (
          <VisibilityToggle
            appointmentTypeDetails={cellValue}
            value={!!cellValue.details?.sobs}
            disabled={!cellValue.details?.act}
            trackingId={SETTINGS_TRACKING_IDS.appointmentTypes.onlineSchedulingToggleButton}
          />
        ),
        sortType: (a: Row<AppointmentType>, b: Row<AppointmentType>) =>
          Number(a.original?.details?.sobs || 0) - Number(b.original?.details?.sobs || 0),
        minWidth: 100,
        maxWidth: 180,
      },
    ],
    [
      t,
      canUseAppointmentColors,
      showSourceColumn,
      sourceColumnAccessor,
      sourceColumnCellRenderer,
      isIntegratedOffice,
      isPaymentsEnabled,
      isOnlineSchedulingEnabled,
      isSmallMode,
      mappingList,
      globalBookingDeposit,
    ]
  );

  return (
    <>
      <Table
        isLoading={isLoading}
        colConfig={tableColumns}
        styleConfig={{
          rows: (row: AppointmentType) => {
            const isDisabled = row.details?.act === null || !row.details?.act;
            const disabledStyle = css`
              color: ${theme.font.colors.disabled};
            `;

            return css`
              ${(isDisabled && disabledStyle) || ''}
              min-height: ${theme.spacing(7.2)};
            `;
          },
        }}
        data={data?.appointmentTypes || []}
        isPaginated
        hasResponsiveColWidths
        rowActions={rowActions}
        bulkActions={bulkActions}
        isSelectable={canUseAppointmentColors}
        isRowDisabled={(row: AppointmentType) => isIntegratedOffice && !row.details?.act}
        rowSelectionConfig={{
          isRowDisabled(rowData) {
            return !rowData.details?.act;
          },
          onSelectionToggle(_, data) {
            setSelectedAppointmentTypes(data);
          },
        }}
        hasGlobalSearch
        globalSearchConfig={{
          initialValue: '',
          placeholder: t('Search Appointment Types Name'),
          searchHandler: handleSearch,
        }}
        tableActions={[
          {
            Icon: () => <Icon name='update' />,
            label: t('Refetch'),
            onClick: handleTableRefetch,
            type: 'icon',
          },
        ]}
        manualPaginationConfig={{
          showNumRowSelector: true,
          showGoToPageField: true,
          page: pageConfig.page || 1,
          defaultRowsPerPage: pageConfig.limit,
          rowsPerPageOptions: [10, 20, 30, 40, 50],
          hasNext: (pageConfig.page || 1) < Math.ceil((totalCount ?? 0) / pageConfig.limit),
          hasPrevious: pageConfig.page > 1,
          total: Math.ceil((totalCount ?? 0) / pageConfig.limit),
          onNumRowsChange: (pageSize) => {
            setPageConfig((prev) => ({ ...prev, limit: pageSize, page: 1 }));
          },
          handleChange: (pageNumber: number) => {
            setPageConfig((prev) => ({
              ...prev,
              page: pageNumber,
            }));
          },
        }}
        {...(!isLoading &&
          !debouncedSearchValue && {
            emptyStateConfig: {
              type: 'schedule_requests',
              header: '',
              description: () => (
                <div css={{ textAlign: 'center' }}>
                  <Text color='subdued'>{t('Ready to start scheduling?')}</Text>
                  <Text color='subdued'>{t('Set up your first appointment type to start taking bookings.')}</Text>
                </div>
              ),
            },
          })}
      />
    </>
  );
};

const DisplayNameHeader = () => {
  const { t } = useTranslation('scheduleAppointmentTypes');

  const { isOnlineSchedulingEnabled } = useAppointmentTypesHelperShallowStore('isOnlineSchedulingEnabled');

  const { tooltipProps, triggerProps, Tooltip } = useTooltip({
    placement: 'bottom-start',
  });

  return (
    <>
      <header css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
        <Text size='medium' css={{ color: theme.colors.neutral40 }}>
          {t('Display Name')}
        </Text>
        <span {...triggerProps}>
          <Icon name='info-small' />
        </span>
      </header>

      <Tooltip {...tooltipProps}>
        {isOnlineSchedulingEnabled
          ? t('This name will be visible to customers when booking online, and on your Weave calendar.')
          : t('This name will be visible on your Weave calendar.')}
      </Tooltip>
    </>
  );
};
